import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { BaseService } from '../../abstract/base.service';
import { SponsorPointRate } from '../data-models/sponsor-point-rate.model';
import { Sponsor } from '../data-models/sponsor.model';
import { SponsorCreateRequest } from '../request-models/sponsor-create-request.model';
import { SponsorFilters } from '../request-models/sponsor-filters.model';
import { SponsorUpdateRequest } from '../request-models/sponsor-update-request.model';
import { Page } from '../response-models/page.model';
import { SponsorWithRates } from '../response-models/sponsor-with-rates.model';
import { isRecord } from '../utils/is-record';
import { toSponsorCreateRequest, toSponsorUpdateRequest } from '../utils/sponsor-request-utils';

/**
 * ADN chemical sponsors: `/admin/sponsors` (hub `admin-sponsors-controller.ts`), paged with `limit`.
 * Rates, locations and representatives have their own nested services; the logo pre-sign is on
 * `FileUploadService`.
 */
@Injectable({ providedIn: 'root' })
export class SponsorsService extends BaseService<Sponsor> {
  constructor() {
    super('admin/sponsors', Sponsor);
  }

  /** GET /admin/sponsors: one page of sponsors. A blank `query` is not sent. */
  listSponsors(filters: SponsorFilters = {}): Observable<Page<Sponsor>> {
    const query = { page: filters.page, limit: filters.limit, query: filters.query?.trim() || undefined };
    return this.getPage({ query });
  }

  /** GET /admin/sponsors/:id: the sponsor and its point rates. Emits `null` when the hub returns no data. */
  getSponsor(sponsorId: string): Observable<SponsorWithRates | null> {
    return this.request('GET', { id: sponsorId }, toSponsorWithRates);
  }

  /** POST /admin/sponsors with only the fields the hub reads. */
  createSponsor(fields: SponsorCreateRequest): Observable<Sponsor | null> {
    return this.create(toSponsorCreateRequest(fields));
  }

  /** PUT /admin/sponsors/:id with only the fields that are set (`null` clears one). */
  updateSponsor(sponsorId: string, fields: SponsorUpdateRequest): Observable<Sponsor | null> {
    return this.update(sponsorId, toSponsorUpdateRequest(fields));
  }

  /** DELETE /admin/sponsors/:id (archive). */
  archiveSponsor(sponsorId: string): Observable<void> {
    return this.delete(sponsorId);
  }
}

function toSponsorWithRates(data: unknown): SponsorWithRates | null {
  if (!isRecord(data)) {
    return null;
  }
  const rates = Array.isArray(data['rates']) ? data['rates'].map(item => new SponsorPointRate(item)) : [];
  return { sponsor: new Sponsor(data), rates };
}
